import { modalActions } from '@/actions';
import Header from '@/components/elements/Header/Header';
import Select from '@/components/elements/forms/Select/Select';
import Button from '@/components/elements/forms/buttons/Button/Button';
import ListInput from '@/components/elements/forms/listInput/ListInput/ListInput';
import ListInputSelect from '@/components/elements/forms/listInput/ListInputSelect/ListInputSelect';
import ListInputTextArea from '@/components/elements/forms/listInput/ListInputTextArea/ListInputTextArea';
import { ListItem, ListItemContent } from '@/components/elements/lists';
import Alert from '@/components/elements/notifications/Alert/Alert';
import PillTabBar from '@/components/elements/tabs/PillTabBar/PillTabBar';
import Icon from '@/components/icons/Icon';
import CardWrapper from '@/components/modules/CardWrapper';
import { capitalizeFirstLetter, classnames, isEmpty, isZipcode } from '@/helpers';
import { guestCheckoutManager, isStandardFormField } from '@/helpers/checkout';
import { isNationalId } from '@/helpers/utilsTS';
import { useAppDispatch } from '@/hooks';
import useCheckoutExperiment from '@/hooks/experiment/useCheckoutExperiment';
import { _s, _t } from '@/locale';
import { FormField } from '@/types/state/shared';
import { Fragment, useEffect, useState } from 'react';
import { UseFormRegister } from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';
import isLength from 'validator/lib/isLength';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { UseCheckoutFormContext } from './CheckoutFormFields.hooks';

const baseTranslationKey = 'components.modules.checkout.CheckoutFormFields';

export const PROFILE_FORM_FIELD_ID = {
  STREET_ADDRESS: 'streetAddress',
  POSTAL_CODE: 'postalCode',
  LOCALITY: 'locality',
  FAMILY_NAME: 'familyName',
  GIVEN_NAME: 'givenName',
  EMAIL: 'email',
  MOBILE: 'mobile',
  PHONE: 'phone',
} as const;

export const DEFAULT_FORM_FIELD_ID = {
  BOOKING_NOTES: 'bookingNotes',
  CAPACITY: 'capacity',
  GIFTCARD: 'giftCard',
  NATIONAL_ID: 'nationalId',
  DISCOUNT_CODE: 'discountCode',
  ...PROFILE_FORM_FIELD_ID,
} as const;

export const GUEST_FORM_FIELD_ID = 'guestName';

type CheckoutFormProps = {
  formId?: string;
  formFields: FormField[];
  context: UseCheckoutFormContext;
  initialFormValues?: Record<string, string>;
  isGuestCheckout?: boolean;
  onUpdateFormField: (formId: string, shouldStoreInSession: boolean, value: Record<string, string>) => void;
  useCardWrapper?: boolean;
};

export type ReactHookFormRegister<T> = {
  register?: UseFormRegister<T>;
};

const CheckoutFormFields = ({
  context,
  formFields,
  initialFormValues,
  isGuestCheckout = false,
  onUpdateFormField,
  useCardWrapper = true,
}: CheckoutFormProps) => {
  const { watch, setValue, formState } = context;
  const dispatch = useAppDispatch();
  const { variant: checkoutVariant } = useCheckoutExperiment();
  const isCheckoutRedesign = checkoutVariant !== 'control';

  const [contactFields, extraFields] = formFields.reduce(
    (acc, field) => {
      if (isStandardFormField(field.id) && field.id !== 'bookingNotes' && field.id !== 'discountCode') {
        acc[0].push(field);
      } else {
        acc[1].push(field);
      }
      return acc;
    },
    [[], [], []],
  );
  const [isMediator, setIsMediator] = useState(guestCheckoutManager().getOptions()?.isMediator ?? false);

  const handleChangeIsMediator = (isMediator: boolean) => {
    if (isMediator) {
      setValue(GUEST_FORM_FIELD_ID, undefined);
      onUpdateFormField('1', false, { guestName: undefined });
    }
    const manager = guestCheckoutManager();
    const options = manager.getOptions();
    manager.setOptions({ ...options, isMediator });
    setIsMediator(isMediator);
  };

  useEffect(() => {
    const subscription = watch((value, e) => {
      onUpdateFormField('1', false, { [e.name]: value[e.name] });
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, onUpdateFormField]);

  useEffect(() => {
    /**
     * if we have formValues already on mount it means we have a saved checkout state
     * and we need to set the values to the form
     */
    Object.entries(initialFormValues ?? []).forEach(([key, value]) => {
      setValue(key, value);
      onUpdateFormField('1', false, { [key]: value });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wrap = (fields: 'extraFields' | 'contactFields' | 'combined', children: React.ReactNode) => {
    let errorsInSection = false;
    /**
     * set error indicator on correct Card
     */
    if (!isEmpty(formState.errors) && checkoutVariant === 'v2') {
      const fieldErrorKeys = Object.keys(formState.errors);

      if (fields === 'contactFields') {
        errorsInSection = contactFields.map((field) => field.id).some((key) => fieldErrorKeys.includes(key));
      } else if (fields === 'extraFields') {
        errorsInSection = extraFields.map((field) => field.id).some((key) => fieldErrorKeys.includes(key));
      } else if (fields === 'combined') {
        // Check errors in both contact and extra fields
        const allFieldIds = [...contactFields, ...extraFields].map((field) => field.id);
        errorsInSection = allFieldIds.some((key) => fieldErrorKeys.includes(key));
      }
    }

    return useCardWrapper ? (
      <CardWrapper className={classnames(errorsInSection && 'border-border_negative !border-[1px]')}>
        {children}
      </CardWrapper>
    ) : (
      <>{children}</>
    );
  };

  if (isGuestCheckout) {
    return isCheckoutRedesign ? (
      <>
        {wrap(
          'contactFields',
          <div className="pb-lg">
            <div className="px-lg pt-md">
              <div className="flex flex-col items-center md:flex-row">
                <Header label={_s(`${baseTranslationKey}.guest.title`)} size="md" className="my-md" />
                <PillTabBar
                  className="w-full md:w-auto"
                  items={[
                    {
                      label: _s(`${baseTranslationKey}.guest.tab.guest`),
                      onClick: () => handleChangeIsMediator(false),
                    },
                    {
                      label: _s(`${baseTranslationKey}.guest.tab.client`),
                      onClick: () => handleChangeIsMediator(true),
                    },
                  ]}
                  defaultSelected={isMediator ? 1 : 0}
                />
              </div>
              {!isMediator && (
                <div className="py-2xl">
                  <Alert
                    variant="information"
                    verticalAlign="top"
                    leftSlot={<Icon variant="info-circle" color="fg_info_bold" />}
                    middleSlot={
                      <div className="space-y-xxs flex flex-1 flex-col">
                        <p className="text-m">{_s(`${baseTranslationKey}.guest.alert.body`)}</p>
                        <div className="mt-md flex">
                          <Button
                            className="!px-0"
                            onClick={() =>
                              dispatch(modalActions.login({ show: true, triggerSource: 'checkout_booking_summary' }))
                            }
                            variant="link">
                            {_s(`${baseTranslationKey}.guest.alert.action`)}
                          </Button>
                        </div>
                      </div>
                    }
                  />
                </div>
              )}
            </div>
            {isMediator && (
              <Fragment>
                <div className="pl-lg py-md">
                  <Header
                    label={_s(`${baseTranslationKey}.guest.header.guest`)}
                    size="sm"
                    className="text-fg_secondary font-light"
                  />
                </div>
                <CheckoutFormField
                  label="Namn"
                  type="text"
                  values={[]}
                  id={GUEST_FORM_FIELD_ID}
                  context={context}
                  floatStyle={isCheckoutRedesign}
                />
              </Fragment>
            )}
            {contactFields.length > 0 && (
              <Fragment>
                {isMediator && (
                  <div className="pl-lg py-md mt-md">
                    <Header
                      label={_s(`${baseTranslationKey}.guest.header.client`)}
                      size="sm"
                      className="text-fg_secondary font-light"
                    />
                  </div>
                )}
                <div className="px-lg gap-x-md gap-y-lg flex flex-col md:grid md:grid-cols-2">
                  {contactFields.map((field) => {
                    return (
                      <Fragment key={field.id}>
                        <CheckoutFormField
                          {...field}
                          context={context}
                          floatStyle={isCheckoutRedesign}
                          leftPadding={false}
                          rightPadding={false}
                        />
                      </Fragment>
                    );
                  })}
                </div>
              </Fragment>
            )}
          </div>,
        )}

        {extraFields.length > 0 &&
          wrap(
            'extraFields',
            <div className="pb-lg">
              <div className="pl-lg py-md">
                <Header label={_s(`${baseTranslationKey}.header.extra-fields`)} size="lg" />
              </div>
              <div className="space-y-sm flex flex-col">
                {extraFields.map((field) => {
                  return (
                    <Fragment key={field.id}>
                      <CheckoutFormField {...field} context={context} floatStyle={isCheckoutRedesign} />
                    </Fragment>
                  );
                })}
              </div>
            </div>,
          )}
      </>
    ) : (
      <>
        {wrap(
          'contactFields',
          <div className="pb-lg">
            <div className="px-lg">
              <div className="py-md space-y-xs flex flex-col">
                <Header label={_s(`${baseTranslationKey}.guest.title`)} size="md" />
                <p className="text-black-600 text-sm">{_s(`${baseTranslationKey}.guest.description`)}</p>
              </div>
              <Alert
                variant="information"
                body={_s(`${baseTranslationKey}.guest.alert.body`)}
                action={{
                  children: _s(`${baseTranslationKey}.guest.alert.action`),
                  onClick: () =>
                    dispatch(modalActions.login({ show: true, triggerSource: 'checkout_booking_summary' })),
                }}
              />
            </div>
            <ListItem
              onClick={() => handleChangeIsMediator(false)}
              leftSlot={<Icon variant={isMediator ? 'radio-unchecked' : 'radio-checked'} />}>
              <ListItemContent title={_s(`${baseTranslationKey}.guest.radio.guest`)} />
            </ListItem>
            <ListItem
              onClick={() => handleChangeIsMediator(true)}
              leftSlot={<Icon variant={isMediator ? 'radio-checked' : 'radio-unchecked'} />}>
              <ListItemContent title={_s(`${baseTranslationKey}.guest.radio.client`)} />
            </ListItem>
            {isMediator && (
              <Fragment>
                <div className="pl-lg py-md">
                  <Header label={_s(`${baseTranslationKey}.guest.header.guest`)} size="md" />
                </div>
                <CheckoutFormField label="Namn" type="text" values={[]} id={GUEST_FORM_FIELD_ID} context={context} />
              </Fragment>
            )}
            {contactFields.length > 0 && (
              <Fragment>
                <div className="pl-lg py-md">
                  <Header
                    label={
                      isMediator
                        ? _s(`${baseTranslationKey}.guest.header.client`)
                        : _s(`${baseTranslationKey}.header.contact-fields.guest`)
                    }
                    size="md"
                  />
                </div>
                <div className="flex flex-col md:grid md:grid-cols-2">
                  {contactFields.map((field) => {
                    return (
                      <Fragment key={field.id}>
                        <CheckoutFormField {...field} context={context} />
                      </Fragment>
                    );
                  })}
                </div>
              </Fragment>
            )}
          </div>,
        )}

        {extraFields.length > 0 &&
          wrap(
            'extraFields',
            <div className="pb-lg">
              <div className="pl-lg py-md">
                <Header label={_s(`${baseTranslationKey}.header.extra-fields`)} size="md" />
              </div>
              <div className="flex flex-col md:grid md:grid-cols-2">
                {extraFields.map((field) => {
                  return (
                    <Fragment key={field.id}>
                      <CheckoutFormField {...field} context={context} />
                    </Fragment>
                  );
                })}
              </div>
            </div>,
          )}
      </>
    );
  }

  // Merge contact and extra fields into single card with single title
  const allFields = [...contactFields, ...extraFields];

  if (allFields.length === 0) {
    return null;
  }

  return wrap(
    'combined',
    <div className="pb-lg">
      <div className="pl-lg py-md">
        <Header label={_s(`${baseTranslationKey}.header.extra-fields`)} size="lg" />
      </div>
      <div className={`flex flex-col ${isCheckoutRedesign ? 'space-y-sm' : 'md:grid md:grid-cols-2'}`}>
        {allFields.map((field) => (
          <Fragment key={field.id}>
            <CheckoutFormField {...field} context={context} floatStyle={isCheckoutRedesign} />
          </Fragment>
        ))}
      </div>
    </div>,
  );
};

const CheckoutFormField = ({
  id,
  type,
  values,
  label: _label,
  required,
  context,
  floatStyle = false,
  leftPadding = true,
  rightPadding = true,
}: FormField & { context: UseCheckoutFormContext } & {
  floatStyle?: boolean;
  leftPadding?: boolean;
  rightPadding?: boolean;
}) => {
  const {
    register,
    watch,
    formState: { errors },
  } = context;
  const value = watch(id, false);
  const { variant: checkoutVariant } = useCheckoutExperiment();

  const errorMessageToString = typeof errors[id]?.message === 'string' ? (errors[id]?.message as string) : '';

  const label = _label ?? _s(capitalizeFirstLetter(id));
  const textFieldPlaceholder = id === 'bookingNotes' ? _s('BookingNotesPlaceholder') : `${_s('Enter')} ${label}`;
  const requiredFieldLabel =
    checkoutVariant === 'v2' ? _t('requiredFieldForBooking') : _t('requiredFieldMissing', { label });

  switch (type) {
    case 'dropdown': {
      return (
        <ListInputSelect
          id={id}
          label={`${label}${required ? '*' : ''}`}
          floatStyle={floatStyle}
          leftPadding={leftPadding}
          error={errorMessageToString}
          value={value}
          {...register(id, {
            ...(required ? { required: requiredFieldLabel } : {}),
            validate: (value) => validateFormFields(id, value),
          })}>
          <Select.Option value="">{_s('Choose')}...</Select.Option>
          {values.map((value, key) => (
            <Select.Option key={key} value={value}>
              {value}
            </Select.Option>
          ))}
        </ListInputSelect>
      );
    }
    case 'text': {
      switch (id) {
        case DEFAULT_FORM_FIELD_ID.BOOKING_NOTES: {
          return (
            <div className="col-span-2">
              <ListInputTextArea
                error={errorMessageToString}
                id={id}
                label={`${label}${required ? '*' : ''}`}
                floatStyle={floatStyle}
                leftPadding={leftPadding}
                rightPadding={rightPadding}
                placeholder={textFieldPlaceholder}
                rows={2}
                {...register(id, {
                  ...(required ? { required: requiredFieldLabel } : {}),
                  validate: (value) => validateFormFields(id, value),
                })}
              />
            </div>
          );
        }
        default: {
          return (
            <ListInput
              label={`${label}${required ? '*' : ''}`}
              floatStyle={floatStyle}
              placeholder={textFieldPlaceholder}
              error={errorMessageToString}
              leftPadding={leftPadding}
              rightPadding={rightPadding}
              id={id}
              {...register(id, {
                ...(required ? { required: requiredFieldLabel } : {}),
                validate: (value) => validateFormFields(id, value),
              })}
            />
          );
        }
      }
    }
    default:
      return null;
  }
};

type IsValid = true;
type IsError = string;

const validateFormFields = (id: FormField['id'], value: string): IsValid | IsError => {
  if (value) {
    switch (id) {
      case 'email':
        return !isEmail(value.trim(), { allow_utf8_local_part: false }) ? _s('invalidEmail') : true;
      case 'mobile':
      case 'phone':
        return !isMobilePhone(value.trim()) || !isLength(value.trim(), { min: 10, max: 14 })
          ? _s('invalidPhone')
          : true;
      case 'nationalId':
        return !isNationalId(value.trim()) ? _s('invalidNationalId') : true;
      case 'postalCode':
        return !isZipcode(value.trim()) ? _s('invalidZipcode') : true;
      default:
        break;
    }
  }
  return true;
};

export default CheckoutFormFields;
